import Button from '@/components/Buttons/Button';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import RemoveWatermarkToggle from '@/components/RemoveWatermarkToggle/RemoveWatermarkToggle';
import { toast } from '@/components/toast';
import { minutesToCreditsDuration, pricingPageUrl } from '@/environment';
import { useIsSubtitlesEnabled } from '@/hooks/useIsSubtitlesEnabled';
import { useRemoveWatermarkAccess } from '@/hooks/useRemoveWatermarkAccess';
import { useUnlimitedVideosEnabled } from '@/hooks/useUnlimitedVideosEnabled';
import flashLightning from '@/images/FlashLightning.svg';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import {
    CacheStatus,
    getPageProperty,
    PolotnoStoreActivePageKeys,
    PolotnoStoreKeys,
    projectLayersLoading as projectLayersLoadingAtom,
    usePolotnoState,
} from '@/pages/AdvancedEditor/hooks';
import { useProjectRenderer } from '@/pages/AdvancedEditor/hooks/useProjectRenderer';
import { getUpgradeReason, isMissingCredits } from '@/pages/AdvancedEditor/utils';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { creditsText, creditsToVideoMinutes, useCreditQuery } from '@/state/credits/credits';
import { useUserSettings } from '@/state/user-settings/UserSettingsProvider';
import theme from '@/theme';
import { StyleCollection } from '@/utils/types';
import { PlanGroup, ScriptType, VoiceAccess } from '@d-id/types';
import styled from '@emotion/styled';
import { ClickAwayListener, Collapse, Divider, List, ListItem, ListItemText, useTheme } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { SubtitleToggle } from './SubtitleToggle';
import { UpgradeModal } from './UpgradeModal';
import { VideoTitle } from './VideoTitle';

const StyledList = styled(List)`
    width: 263px;
    display: flex;
    flex-direction: column;
    padding: 16px;
    margin-right: 12px;
    margin-top: -8px;
    background: white;
    border-radius: 12px;
    box-shadow: 0px 10px 60px 0px rgba(0, 0, 0, 0.1);
    gap: 16px;
`;

const StyledListItem = styled(ListItem)`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const ToggleList = styled('ul')`
    margin: 0;
    padding: 0;
    list-style-type: none;
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
`;

const StyledListItemButton = styled(ListItem)`
    display: flex;
    justify-content: center;
`;

const StyledListItemText = styled(ListItemText)`
    & .MuiTypography-root {
        font-size: 1.4rem;
        font-weight: 600;
        text-align: left;
    }
`;

const StyledFreeCredits = styled(ListItemText)`
    & .MuiTypography-root {
        font-size: 1.4rem;
        font-weight: 700;
        text-align: right;
        color: ${({ theme }) => theme.colors.primary[900]};
    }
`;

const CreditsCost = styled.span<{ missing?: boolean }>`
    color: ${({ missing }) => (missing ? 'red' : 'black')};
    font-weight: ${({ missing }) => (missing ? 'bold' : 'normal')};
    font-size: 1.4rem;
    text-align: right;
`;

const CreditsLeft = styled.span<{ warning: boolean }>`
    color: ${({ warning }) => (warning ? theme.palette.error.main : '#7c7c7a')};
    font-size: 1.4rem;
`;

const CtaButton = styled(Button)`
    width: 100%;
    height: 3.4rem;
    font-size: 1.4rem;
    font-weight: bold;
    text-transform: none;
    line-height: 1.8rem;
    border-radius: 8px;
`;

const ToggleFieldContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const PremiumBadge = styled(MaskedIcon)`
    margin-left: 0.4rem;
`;

const styles: StyleCollection = {
    skeletonStyle: {
        width: '5rem',
        height: '1.6rem',
        margin: '0.4rem 0 0 0',
        flexGrow: 1,
    },
};

enum UpgradeReason {
    CREDITS = 'credits',
    SUBTITLES = 'subtitles',
    PREMIUM_VOICE = 'premium-voice',
    NONE = 'none',
}

enum GenerateState {
    Loading = 'loading',
    Ready = 'ready',
    Generating = 'generating',
}

interface ICustomSkeleton {
    showText: any;
    children: any;
    style: StyleCollection[0];
}
function TextOrSkeleton({ showText, children, style }: ICustomSkeleton) {
    if (showText) {
        return <span style={{ fontSize: '1.2rem', flexGrow: 1, textAlign: 'right' }}>{children}</span>;
    }

    return <Skeleton component="div" variant="rectangular" sx={style} />;
}

interface Props {
    onCtaClick: () => void;
    onClickAway: () => void;
    duration?: number;
    isProcessing: boolean;
    pageIds: string[];
}

export const GenerateDropdown = observer(({ onCtaClick, onClickAway, duration, isProcessing, pageIds }: Props) => {
    const theme = useTheme();
    const { store } = useAdvancedEditorContext();
    const { user } = useAuth();
    const { enabled: unlimitedVideoCreationEnabled } = useUnlimitedVideosEnabled(user);
    const creditsToMinutesEnabled = useFlag('credits_to_minutes_enabled', false);

    const [subtitlesActive] = usePolotnoState(PolotnoStoreKeys.subtitlesActive);

    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [showIncreasePackageModal, setShowIncreasePackageModal] = useState(false);
    const [generateState, setGenerateState] = useState(GenerateState.Loading);

    const [projectPagesImages] = usePolotnoState(PolotnoStoreKeys.projectPagesImages);
    const projectLayersLoading = useRecoilValue(projectLayersLoadingAtom);

    const projectRenderer = useProjectRenderer();
    const subtitlesEnabled = useIsSubtitlesEnabled();
    const { data: userCredits } = useCreditQuery();
    const { userSettings, updateUserSettings, userSettingsUpdating } = useUserSettings();

    const { showRemoveWatermark, removeWatermarkBlocked } = useRemoveWatermarkAccess(user);
    const initialRemoveWatermarkSelected = useRef<boolean>(!!userSettings?.preferences?.removeWatermark);
    const [removeWatermarkSelected, setRemoveWatermarkSelected] = useState(!!userSettings?.preferences?.removeWatermark);

    const creditsCost = duration ? Math.ceil(duration / minutesToCreditsDuration) : null;
    const hasEnoughCredits = (userCredits?.remaining ?? 0) >= (creditsCost ?? 0);
    const requiredCredits = !hasEnoughCredits && creditsCost && userCredits?.remaining! - creditsCost;

    const isPremiumUser = [PlanGroup.PRO, PlanGroup.ADVANCED, PlanGroup.ENTERPRISE, PlanGroup.ENTERPRISE_TRIAL].includes(user?.planGroup!);
    const isTrialUser = user?.planGroup === PlanGroup.TRIAL;

    const upgradeReason = getUpgradeReason(
        isPremiumUser,
        subtitlesActive,
        creditsCost,
        userCredits,
        store.pages.some(
            p =>
                getPageProperty(p, PolotnoStoreActivePageKeys.selectedScript) === ScriptType.text &&
                getPageProperty(p, PolotnoStoreActivePageKeys.ttsSelectedVoice)?.access === VoiceAccess.Premium
        )
    );
    const missingPageImage = projectRenderer ? false : pageIds.some(pageId => !projectPagesImages[pageId]?.url);
    const ttsAudioCacheLoading = store.pages.some(
        page => getPageProperty(page, PolotnoStoreActivePageKeys.ttsAudioCacheStatus) === CacheStatus.Updating
    );
    const audioLoading = store.pages.some(
        page =>
            getPageProperty(page, PolotnoStoreActivePageKeys.isUploadingAudio) ||
            getPageProperty(page, PolotnoStoreActivePageKeys.isRecordUploading)
    );

    const generateLoading =
        isProcessing || !userCredits || !creditsCost || !duration || missingPageImage || userSettingsUpdating || audioLoading;

    useEffect(() => {
        if (!generateLoading) {
            setGenerateState(GenerateState.Ready);
        }
    }, [generateLoading]);

    useEffect(() => {
        if (removeWatermarkSelected && showRemoveWatermark && !removeWatermarkBlocked) {
            setGenerateState(GenerateState.Loading);
            updateUserSettings({ preferences: { removeWatermark: true } }).catch(() => {
                setRemoveWatermarkSelected(false);
                toast.error({ title: 'Failed to update watermark preference, please try again' });
            });
        }
    }, [removeWatermarkSelected, showRemoveWatermark, removeWatermarkBlocked]);

    useEffect(() => {
        const layersLoading = projectRenderer ? false : projectLayersLoading;
        if (generateState === GenerateState.Generating && !layersLoading && !ttsAudioCacheLoading) {
            onCtaClick();
        }
    }, [generateState, projectLayersLoading, ttsAudioCacheLoading, projectRenderer, removeWatermarkSelected]);

    const generateText = () => (isTrialUser && isMissingCredits(userCredits, creditsCost) ? 'Upgrade' : 'Generate');

    const handleGenerate = () => {
        if (upgradeReason === UpgradeReason.SUBTITLES || upgradeReason === UpgradeReason.PREMIUM_VOICE) {
            setShowUpgradeModal(true);
            return;
        } else if (upgradeReason === UpgradeReason.CREDITS) {
            setShowIncreasePackageModal(true);
            return;
        }
        setGenerateState(GenerateState.Generating);
    };

    const renderCreditSection = () => {
        return (
            <>
                <Collapse in={true} timeout="auto" unmountOnExit>
                    <StyledListItem disablePadding>
                        <StyledListItemText primary={creditsToMinutesEnabled ? 'Minutes' : 'Credits'} />
                        {unlimitedVideoCreationEnabled && !isMissingCredits(userCredits, creditsCost) ? (
                            <StyledFreeCredits data-testid="credits-cost-free" primary="Free" />
                        ) : (
                            <TextOrSkeleton showText={creditsCost && userCredits} style={styles.skeletonStyle}>
                                <CreditsCost data-testid="credits-cost" missing={isMissingCredits(userCredits, creditsCost)}>
                                    {creditsToMinutesEnabled ? creditsToVideoMinutes(creditsCost ?? 0) : creditsCost}
                                </CreditsCost>
                                <CreditsLeft data-testid="credits-left" warning={userCredits!.remaining <= 0}>
                                    {` (${creditsToMinutesEnabled ? creditsToVideoMinutes(userCredits!.remaining) : creditsText(userCredits!.remaining)})`}
                                </CreditsLeft>
                            </TextOrSkeleton>
                        )}
                    </StyledListItem>
                </Collapse>
            </>
        );
    };

    const generateDropdown =
        showUpgradeModal || showIncreasePackageModal ? (
            <></>
        ) : (
            <StyledList>
                <VideoTitle />
                <StyledListItem disablePadding>
                    <StyledListItemText primary="Video duration" />
                    <TextOrSkeleton showText={!!duration} style={styles.skeletonStyle}>
                        {duration ? new Date(duration! * 1000).toISOString().substring(14, 19) : ''}
                    </TextOrSkeleton>
                </StyledListItem>
                {(subtitlesEnabled || (showRemoveWatermark && !initialRemoveWatermarkSelected.current)) && (
                    <>
                        <Divider light variant="fullWidth" />
                        <ToggleList>
                            {subtitlesEnabled && (
                                <StyledListItem disablePadding>
                                    <ToggleFieldContainer>
                                        <StyledListItemText primary="Include subtitles" />
                                        {!isPremiumUser && (
                                            <PremiumBadge src={flashLightning} size="16px" color={theme.colors.primary.main} />
                                        )}
                                    </ToggleFieldContainer>
                                    <SubtitleToggle data-testid="editor-include-subtitles-toggle" />
                                </StyledListItem>
                            )}
                            {showRemoveWatermark && !initialRemoveWatermarkSelected.current && (
                                <StyledListItem disablePadding>
                                    <ToggleFieldContainer>
                                        <StyledListItemText primary="Remove watermark" />
                                        <PremiumBadge
                                            src={flashLightning}
                                            size="16px"
                                            color={isPremiumUser ? theme.colors.secondary.main : theme.colors.primary.main}
                                        />
                                    </ToggleFieldContainer>
                                    <RemoveWatermarkToggle
                                        blocked={removeWatermarkBlocked}
                                        checked={removeWatermarkSelected}
                                        onChange={selected => {
                                            setRemoveWatermarkSelected(selected);
                                        }}
                                    />
                                </StyledListItem>
                            )}
                        </ToggleList>
                    </>
                )}
                <Divider light variant="fullWidth" />
                {renderCreditSection()}
                <StyledListItemButton disablePadding>
                    <CtaButton
                        data-testid="editor-lets-go-button"
                        onClick={() =>
                            isTrialUser && isMissingCredits(userCredits, creditsCost)
                                ? window.open(pricingPageUrl, '_blank')
                                : handleGenerate()
                        }
                        disabled={false}
                        isLoading={['loading', 'generating'].includes(generateState)}
                        loadingText={generateState === 'generating' ? 'Generating' : ''}>
                        {generateText()}
                    </CtaButton>
                </StyledListItemButton>
            </StyledList>
        );

    return (
        <>
            <ClickAwayListener
                onClickAway={() => {
                    if (!showUpgradeModal && !showIncreasePackageModal) onClickAway();
                }}>
                {generateDropdown}
            </ClickAwayListener>
            <UpgradeModal
                upgradeReason={upgradeReason}
                showUpgradeModal={showUpgradeModal}
                setShowUpgradeModal={setShowUpgradeModal}
                showIncreasePackageModal={showIncreasePackageModal}
                setShowIncreasePackageModal={setShowIncreasePackageModal}
                requiredCredits={requiredCredits || undefined}
                onCtaClick={onCtaClick}
            />
        </>
    );
});
